import type { UiStreamAction } from "@intelligems/ig-types";

import { readToolJsonResults } from "./agenticLogicCreationEvents";

/**
 * What a chat-side write invalidated, named in the vocabulary of the cache
 * rather than of the tool: several tools land on the same rows, and the page
 * beside the chat only cares which rows moved.
 *
 * `rule` covers everything keyed to one rule — the rule row, its versions, its
 * draft, and the list it appears in — because every tool that writes one of
 * those can move the others in the same call (a settings write on a never-live
 * rule publishes it; a publish supersedes a version and changes the row's
 * state).
 */
export type AgenticLogicWriteScope =
  | { kind: "rule"; ruleId: string }
  | { kind: "placements" }
  | { kind: "assignments" }
  | { kind: "exclusions" }
  | { kind: "dataset"; key: string | null };

export interface AgenticLogicWriteTracker {
  /** The call's tool + input, kept until its output arrives. */
  pendingByToolCallId: Record<
    string,
    { toolName: string; input: Record<string, unknown> }
  >;
  /** The most recent landed write; a new object identity marks a new one. */
  lastWrite: { scopes: AgenticLogicWriteScope[] } | null;
}

const ruleScope = (ruleId: string | null): AgenticLogicWriteScope[] =>
  ruleId ? [{ kind: "rule", ruleId }] : [];

type ScopeReader = (
  input: Record<string, unknown>,
  results: Record<string, unknown>
) => AgenticLogicWriteScope[];

/**
 * Every Agentic Logic tool whose call leaves the browser's cache behind the
 * server, and the rows each one moves.
 *
 * The rule-scoped entries prefer the id the tool ECHOED and fall back to the
 * one it was CALLED with: a create has no input id, a delete has no output
 * rule, and the rest carry both.
 *
 * `get_agentic_logic_publish_job` is the one read here, and it earns its place:
 * a publish is asynchronous, so the version list does not change when
 * `publish_agentic_logic_rule` returns a job id — it changes when that job
 * reaches `succeeded`. This is the chat's counterpart to the workspace's
 * `onPublishSucceeded` (`agenticApiPort.ts`), which names the same moment for
 * a publish the page itself started.
 */
const WRITE_TOOLS: Record<string, ScopeReader> = {
  create_agentic_logic_rule: (_input, results) =>
    ruleScope(readId(results.rule)),
  update_agentic_logic_rule: (input, results) =>
    ruleScope(readId(results.rule) ?? readNonEmptyString(input.ruleId)),
  save_agentic_logic_rule_draft: (input, results) =>
    ruleScope(readNonEmptyString(input.ruleId) ?? readRuleId(results.draft)),
  publish_agentic_logic_rule: (input) =>
    ruleScope(readNonEmptyString(input.ruleId)),
  get_agentic_logic_publish_job: (input, results) =>
    readJobStatus(results.job) === "succeeded"
      ? ruleScope(readRuleId(results.job) ?? readNonEmptyString(input.ruleId))
      : [],
  agentic_logic_rule_lifecycle: (input, results) =>
    ruleScope(readId(results.rule) ?? readNonEmptyString(input.ruleId)),
  create_agentic_logic_placement: () => [{ kind: "placements" }],
  create_agentic_logic_placement_assignment: () => [{ kind: "assignments" }],
  update_agentic_logic_placement_assignment: () => [{ kind: "assignments" }],
  delete_agentic_logic_placement_assignment: () => [{ kind: "assignments" }],
  update_agentic_logic_exclusions: () => [{ kind: "exclusions" }],
  put_agentic_logic_dataset: (input) => [
    { kind: "dataset", key: readNonEmptyString(input.key) },
  ],
};

export const createAgenticLogicWriteTracker = (): AgenticLogicWriteTracker => ({
  pendingByToolCallId: {},
  lastWrite: null,
});

/**
 * Watches the chat stream for writes the surrounding page is showing.
 *
 * Atlas writes through backend tools, so none of it passes through a client
 * mutation and nothing invalidates react-query on its own. Left alone, a rule
 * page sits on the row it fetched when it mounted: an edit made in the chat is
 * invisible there, including the "Unsaved changes — not live yet" note, which
 * is computed by diffing that row against the active version
 * (`useUnpublishedRuleChanges`) and so reads "no change" off two stale halves.
 *
 * Deliberately separate from `trackAgenticLogicCreation`, and for the same
 * reason as the scenario tracker: that one closes at the write that FINISHES a
 * rule, while these writes go on for the rest of the conversation.
 */
export function trackAgenticLogicWrite(
  tracker: AgenticLogicWriteTracker,
  action: UiStreamAction
): AgenticLogicWriteTracker {
  if (action.type !== "addStreamPart") {
    return tracker;
  }
  const part = action.streamPart;

  if (part.type === "tool-input-available") {
    if (!(part.toolName in WRITE_TOOLS)) {
      return tracker;
    }
    return {
      ...tracker,
      pendingByToolCallId: {
        ...tracker.pendingByToolCallId,
        [part.toolCallId]: {
          toolName: part.toolName,
          input: asRecord(part.input),
        },
      },
    };
  }

  if (part.type !== "tool-output-available") {
    return tracker;
  }
  const call = tracker.pendingByToolCallId[part.toolCallId];
  if (!call) {
    return tracker;
  }

  // Consumed either way: a replayed transcript re-emits the same output, and
  // one call must not invalidate twice.
  const pendingByToolCallId = { ...tracker.pendingByToolCallId };
  delete pendingByToolCallId[part.toolCallId];

  const results = readToolJsonResults(part.output);
  if (!results || isToolError(results)) {
    return { ...tracker, pendingByToolCallId };
  }

  const scopes = WRITE_TOOLS[call.toolName](call.input, results);
  if (scopes.length === 0) {
    return { ...tracker, pendingByToolCallId };
  }

  return { pendingByToolCallId, lastWrite: { scopes } };
}

/**
 * A failed tool answers with `formatToolError`'s `{name, message}`; every
 * success carries `executionTime` from `BaseToolOutputSchema` and never a
 * `name`. That pair is the whole test, which is what lets the deletes — whose
 * successful body is `{}` — count as landed writes.
 */
function isToolError(results: Record<string, unknown>): boolean {
  return typeof results.name === "string" && !("executionTime" in results);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function readId(value: unknown): string | null {
  return value && typeof value === "object"
    ? readNonEmptyString((value as Record<string, unknown>).id)
    : null;
}

function readRuleId(value: unknown): string | null {
  return value && typeof value === "object"
    ? readNonEmptyString((value as Record<string, unknown>).ruleId)
    : null;
}

function readJobStatus(value: unknown): string | null {
  return value && typeof value === "object"
    ? readNonEmptyString((value as Record<string, unknown>).status)
    : null;
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}
